<!-- components/MaintenanceCalendar.vue -->
<template>
  <div class="maintenance-calendar">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold">{{ title }}</h3>
      <div class="flex space-x-2 items-center">
        <button
            @click="previousMonth"
            class="hover:bg-gray-100 rounded"
        >
          <ChevronLeftIcon class="h-5 w-5"/>
        </button>
        <span>
          {{ currentMonthYear }}
        </span>
        <button
            @click="nextMonth"
            class="hover:bg-gray-600 rounded"
        >
          <ChevronRightIcon class="h-5 w-5"/>
        </button>
      </div>
    </div>

    <!-- Calendar Grid -->
    <div class="grid grid-cols-7 gap-1">
      <!-- Day headers -->
      <div v-for="day in weekDays"
           :key="day"
           class="text-center text-sm font-medium text-gray-500 py-2">
        {{ day }}
      </div>

      <!-- Calendar days -->
      <div v-for="date in calendarDays"
           :key="date.toISOString()"
           class="aspect-square p-1 border rounded"
           :class="getDayClass(date)">
        <div class="h-full flex flex-col">
          <span class="text-sm" :class="{'text-gray-500': !isCurrentMonth(date)}">
            {{ date.getDate() }}
          </span>

          <!-- Maintenance Tasks -->
          <div class="flex-1 overflow-y-auto">
            <div v-for="task in getTasksForDate(date)"
                 :key="task.id"
                 @click.stop="$emit('onEventSelect', task)"
                 class="text-xs p-1 mb-1 rounded"
                 :class="getTaskClass(task)">
              {{ task.title }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Task Legend -->
    <div class="mt-4 flex gap-4 text-sm">
      <div class="flex items-center">
        <span class="w-3 h-3 rounded-full bg-blue-200 mr-2"></span>
        Planned
      </div>
      <div class="flex items-center">
        <span class="w-3 h-3 rounded-full bg-green-200 mr-2"></span>
        Completed
      </div>
      <div class="flex items-center">
        <span class="w-3 h-3 rounded-full bg-red-200 mr-2"></span>
        Overdue
      </div>
      <div class="flex items-center">
        <span class="w-3 h-3 rounded-full bg-gray-200 mr-2"></span>
        Open
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, computed} from 'vue'
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/vue/24/solid'

const emits = defineEmits(['onDateChanged', 'onEventSelect']);
const props = defineProps({
  tasks: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'Maintenance Schedule'
  },
  dateKey: {
    type: String,
    default: 'start_at'
  }
})

const currentDate = ref(new Date())
const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const currentMonthYear = computed(() => {
  return currentDate.value.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  })
})

const calendarDays = computed(() => {
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)

  // Get days from previous month to fill first week
  const daysFromPrevMonth = firstDay.getDay()
  const daysFromNextMonth = 6 - lastDay.getDay()

  const days = []

  // Add days from previous month
  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    days.push(new Date(year, month, -i))
  }

  // Add days from current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(new Date(year, month, i))
  }

  // Add days from next month
  for (let i = 1; i <= daysFromNextMonth; i++) {
    days.push(new Date(year, month + 1, i))
  }

  return days
})

const getTasksForDate = (date) => {
  return props.tasks.filter(task => {
    const taskDate = new Date(task[props.dateKey])
    return taskDate.toDateString() === date.toDateString()
  })
}

const isCurrentMonth = (date) => {
  return date.getMonth() === currentDate.value.getMonth()
}

const getDayClass = (date) => {
  return {
    'bg-gray-50': !isCurrentMonth(date),
    'bg-blue-600 text-black today': isToday(date),
    'hover:bg-gray-100': true
  }
}

const getTaskClass = (task) => {
  return {
    'bg-blue-200': task.status === 'planned',
    'bg-green-200': task.status === 'completed',
    'bg-gray-200 text-black': task.status === 'open',
    'bg-red-200': isOverdue(task)
  }
}

const isToday = (date) => {
  const today = new Date()
  return date.toDateString() === today.toDateString()
}

const isOverdue = (task) => {
  const today = new Date()
  const taskDate = new Date(task.end_at)
  return taskDate < today && task.status !== 'completed'
}

const previousMonth = () => {
  currentDate.value = new Date(
      currentDate.value.getFullYear(),
      currentDate.value.getMonth() - 1,
      1
  )
  emits('onDateChanged', currentDate.value);
}

const nextMonth = () => {
  currentDate.value = new Date(
      currentDate.value.getFullYear(),
      currentDate.value.getMonth() + 1,
      1
  )
  emits('onDateChanged', currentDate.value);

}
</script>

<style scoped>
.maintenance-calendar {
  @apply bg-white rounded-lg p-4;
}

/* Add custom scrollbar for task overflow */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #CBD5E0 #EDF2F7;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #EDF2F7;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #CBD5E0;
  border-radius: 2px;
}
</style>